import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ShiftReconciliation } from '../../shiftsMeta';

type ShiftReconciliationBarProps = {
    reconciliation: ShiftReconciliation;
};

/**
 * Whether the drawer and the books agree, in the Registers style (BOS-164).
 *
 * The Shifts page already promises "the combined ledger balance of any one of
 * them", and until bos-api booked the close difference that promise was only
 * half true: the counted figure lived on the shift and nowhere in the ledger. A
 * closed shift reconciles when its difference reached the books — a nil
 * difference needed no entry, and a real one has an entry to point at.
 *
 * A shift closed **before** the difference was posted at all is reported as its
 * own case rather than as a failure. Nothing is wrong with the shift; the ledger
 * simply never heard about it, and saying "not in agreement" about history
 * nobody can now correct would be an alarm with no action behind it.
 *
 * Renders nothing when the page holds no closed shift — there is no claim to
 * make about a page of open drawers.
 */
export const ShiftReconciliationBar: React.FC<ShiftReconciliationBarProps> = ({
    reconciliation,
}) => {
    const { t } = useTranslation('pages');

    if (reconciliation.closed === 0) {
        return null;
    }

    const inAgreement = reconciliation.inAgreement;

    return (
        <div
            className="flex items-start gap-3 rounded-xl border border-separator bg-surface-secondary px-4 py-3"
            role="status"
            {...getNodeTestIdProp('branches-shifts-reconciliation')}
        >
            <Icon
                aria-hidden
                className={`mt-0.5 shrink-0 ${
                    inAgreement ? 'text-success' : 'text-warning'
                }`}
                icon={
                    inAgreement
                        ? 'gravity-ui:circle-check'
                        : 'gravity-ui:triangle-exclamation'
                }
            />
            <div className="flex flex-col gap-1">
                <span
                    className="text-sm font-semibold text-foreground"
                    {...getNodeTestIdProp(
                        'branches-shifts-reconciliation-verdict',
                    )}
                >
                    {t(
                        inAgreement
                            ? 'branches.shifts.reconciliation.inAgreement'
                            : 'branches.shifts.reconciliation.notInAgreement',
                    )}
                </span>
                <span
                    className="text-sm text-muted"
                    {...getNodeTestIdProp(
                        'branches-shifts-reconciliation-detail',
                    )}
                >
                    {t('branches.shifts.reconciliation.booked', {
                        count: reconciliation.booked,
                        closed: reconciliation.closed,
                        amount: formatGel(reconciliation.bookedTetri),
                    })}
                    {reconciliation.legacy > 0
                        ? ` ${t('branches.shifts.reconciliation.legacy', {
                              count: reconciliation.legacy,
                          })}`
                        : ''}
                </span>
            </div>
        </div>
    );
};
